import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, 
  Search, 
  Trash2, 
  Eye, 
  CheckCircle, 
  Clock, 
  X, 
  Mail, 
  Phone, 
  User, 
  Tag, 
  Calendar,
  AlertCircle,
  Box
} from 'lucide-react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  // Top Menu Toggle State: 'contact' | 'product'
  const [inquiryTypeTab, setInquiryTypeTab] = useState('contact');

  // Read inquiries in real-time from Firestore
  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(collection(db, 'inquiries'), (snapshot) => {
      const items = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          status: data.status || 'pending',
          dateString: data.timestamp ? new Date(data.timestamp.seconds * 1000).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'Just now'
        };
      });

      items.sort((a, b) => {
        const tA = a.timestamp ? a.timestamp.seconds : Date.now();
        const tB = b.timestamp ? b.timestamp.seconds : Date.now();
        return tB - tA;
      });

      setInquiries(items);
      setLoading(false);
    }, (error) => {
      console.error("Error listening to inquiries:", error);
      setLoading(false);
    });

    return unsub;
  }, []);

  useEffect(() => {
    if (selectedInquiry) {
      document.body.classList.add('admin-modal-open');
    } else {
      document.body.classList.remove('admin-modal-open');
    }
    return () => document.body.classList.remove('admin-modal-open');
  }, [selectedInquiry]);

  const toggleStatus = async (id, currentStatus) => {
    const nextStatus = currentStatus === 'viewed' ? 'pending' : 'viewed';
    
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true
    });

    try {
      await updateDoc(doc(db, 'inquiries', id), {
        status: nextStatus
      });
      
      if (selectedInquiry && selectedInquiry.id === id) {
        setSelectedInquiry(prev => ({ ...prev, status: nextStatus }));
      }

      Toast.fire({
        icon: 'success',
        title: `Inquiry marked as ${nextStatus.toUpperCase()}`
      });
    } catch (error) {
      console.error("Error updating status:", error);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You will permanently delete this inquiry record!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'inquiries', id));
        if (selectedInquiry && selectedInquiry.id === id) {
          setSelectedInquiry(null);
        }
        Swal.fire({
          title: 'Deleted!',
          text: 'Inquiry has been deleted.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error("Error deleting inquiry:", error);
        Swal.fire('Error', 'Failed to delete inquiry', 'error');
      }
    }
  };

  const isProductInquiry = (item) => Boolean(
    item.productName || 
    item.quantityRequired || 
    (item.subject && item.subject.toLowerCase().includes('product inquiry'))
  );

  // Filter inquiries by selected tab (Contact vs Product) + Search + Status Filter
  const tabInquiries = inquiries.filter(item => {
    if (inquiryTypeTab === 'product') {
      return isProductInquiry(item);
    } else {
      return !isProductInquiry(item);
    }
  });

  const filteredInquiries = tabInquiries.filter(item => {
    const matchesSearch = 
      (item.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.phone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.productName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.message || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Counts for current tab view
  const currentTotal = tabInquiries.length;
  const currentPending = tabInquiries.filter(i => i.status === 'pending').length;
  const currentViewed = tabInquiries.filter(i => i.status === 'viewed').length;

  // Overall counts for tabs
  const contactCount = inquiries.filter(i => !isProductInquiry(i)).length;
  const productCount = inquiries.filter(i => isProductInquiry(i)).length;

  return (
    <div className="space-y-6 pb-20 font-poppins">
      
      {/* Header Banner */}
      <div className="bg-sugarcane-charcoal p-6 rounded-2xl text-white flex flex-col md:flex-row justify-between items-center gap-6 relative overflow-hidden shadow-md">
        <div className="absolute top-0 left-0 w-64 h-64 bg-sugarcane-green/10 rounded-full -ml-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-700/10 rounded-full -mr-32 -mb-32 blur-3xl"></div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-sugarcane-green rounded-xl flex items-center justify-center text-white">
              <MessageSquare size={20} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Inquiries Management</h2>
          </div>
          <p className="text-gray-400 text-sm italic">Track and manage Product and Contact Us inquiries submitted by users.</p>
        </div>

        <div className="relative z-10 px-5 py-2.5 bg-white/5 rounded-xl border border-white/10 flex items-center space-x-4">
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sugarcane-green">Connection</p>
            <p className="font-semibold text-sm">Real-time Sync</p>
          </div>
          <div className="w-2.5 h-2.5 bg-sugarcane-green rounded-full animate-pulse"></div>
        </div>
      </div>

      {/* TOP TOGGLE MENU (Contact Us vs Product Inquiries) */}
      <div className="flex bg-gray-100 p-1.5 rounded-2xl max-w-xl mx-auto border border-gray-200/80 shadow-inner">
        <button
          onClick={() => setInquiryTypeTab('contact')}
          className={`flex-1 py-3 px-4 rounded-xl font-bold uppercase text-xs tracking-wider transition-all duration-300 flex items-center justify-center space-x-2 cursor-pointer ${
            inquiryTypeTab === 'contact'
              ? 'bg-sugarcane-green text-white shadow-md'
              : 'text-gray-500 hover:text-sugarcane-charcoal'
          }`}
        >
          <MessageSquare size={16} />
          <span>Contact Us Inquiries</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ml-1 ${
            inquiryTypeTab === 'contact' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            {contactCount}
          </span>
        </button>

        <button
          onClick={() => setInquiryTypeTab('product')}
          className={`flex-1 py-3 px-4 rounded-xl font-bold uppercase text-xs tracking-wider transition-all duration-300 flex items-center justify-center space-x-2 cursor-pointer ${
            inquiryTypeTab === 'product'
              ? 'bg-sugarcane-green text-white shadow-md'
              : 'text-gray-500 hover:text-sugarcane-charcoal'
          }`}
        >
          <Box size={16} />
          <span>Product Inquiries</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ml-1 ${
            inquiryTypeTab === 'product' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            {productCount}
          </span>
        </button>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div 
          whileHover={{ y: -4 }}
          className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-all duration-300"
        >
          <div>
            <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">
              Total {inquiryTypeTab === 'product' ? 'Product' : 'Contact'} Inquiries
            </p>
            <h3 className="text-3xl font-bold text-sugarcane-charcoal">{currentTotal}</h3>
          </div>
          <div className="w-12 h-12 bg-gray-50 text-sugarcane-charcoal rounded-xl flex items-center justify-center border border-gray-100 shadow-sm">
            {inquiryTypeTab === 'product' ? <Box size={20} /> : <MessageSquare size={20} />}
          </div>
        </motion.div>

        <motion.div 
          whileHover={{ y: -4 }}
          className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-all duration-300"
        >
          <div>
            <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">Pending Followup</p>
            <h3 className="text-3xl font-bold text-amber-600">{currentPending}</h3>
          </div>
          <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center border border-amber-100/50 shadow-sm animate-pulse">
            <Clock size={20} />
          </div>
        </motion.div>

        <motion.div 
          whileHover={{ y: -4 }}
          className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-all duration-300"
        >
          <div>
            <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">Completed / Viewed</p>
            <h3 className="text-3xl font-bold text-sugarcane-green">{currentViewed}</h3>
          </div>
          <div className="w-12 h-12 bg-emerald-50 text-sugarcane-green rounded-xl flex items-center justify-center border border-emerald-100/50 shadow-sm">
            <CheckCircle size={20} />
          </div>
        </motion.div>
      </div>

      {/* Control Panel / Search & Filters */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input 
            type="text"
            placeholder={`Search ${inquiryTypeTab === 'product' ? 'Product' : 'Contact'} Inquiries...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-12 pr-6 py-2.5 focus:ring-2 focus:ring-sugarcane-green/10 focus:border-sugarcane-green focus:bg-white transition-all font-medium text-sm text-sugarcane-charcoal outline-none"
          />
        </div>

        <div className="flex items-center space-x-3 w-full md:w-auto">
          <span className="text-xs font-semibold tracking-wider text-gray-500 hidden lg:inline">Filter By:</span>
          <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-200 w-full md:w-auto">
            {['all', 'pending', 'viewed'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-1.5 rounded-lg font-bold uppercase text-[10px] tracking-wider transition-all duration-300 w-full md:w-auto cursor-pointer ${
                  statusFilter === status 
                    ? 'bg-sugarcane-green text-white shadow-sm' 
                    : 'text-gray-400 hover:text-sugarcane-charcoal'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Tabular Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-20 text-center">
            <div className="w-10 h-10 border-4 border-sugarcane-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400 font-semibold uppercase tracking-wider text-[10px]">Loading Inquiries Collection...</p>
          </div>
        ) : filteredInquiries.length === 0 ? (
          <div className="py-20 text-center max-w-md mx-auto">
            <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-inner">
              <AlertCircle className="text-gray-300" size={32} />
            </div>
            <h3 className="text-lg font-bold text-sugarcane-charcoal mb-1">No Inquiries Found</h3>
            <p className="text-gray-400 text-xs leading-relaxed">
              No {inquiryTypeTab === 'product' ? 'product' : 'contact us'} inquiry records match your filter criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="py-4 px-6 text-xs font-semibold tracking-wider text-gray-500">Date & Time</th>
                  <th className="py-4 px-6 text-xs font-semibold tracking-wider text-gray-500">Full Name</th>
                  <th className="py-4 px-6 text-xs font-semibold tracking-wider text-gray-500">Contact Details</th>
                  {inquiryTypeTab === 'product' ? (
                    <>
                      <th className="py-4 px-6 text-xs font-semibold tracking-wider text-gray-500">Product Name</th>
                      <th className="py-4 px-6 text-xs font-semibold tracking-wider text-gray-500">Quantity Required</th>
                    </>
                  ) : (
                    <>
                      <th className="py-4 px-6 text-xs font-semibold tracking-wider text-gray-500">Subject</th>
                      <th className="py-4 px-6 text-xs font-semibold tracking-wider text-gray-500">Message Content</th>
                    </>
                  )}
                  <th className="py-4 px-6 text-xs font-semibold tracking-wider text-gray-500">Status</th>
                  <th className="py-4 px-6 text-xs font-semibold tracking-wider text-gray-500 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredInquiries.map((inquiry) => (
                  <tr 
                    key={inquiry.id} 
                    className="hover:bg-gray-50/40 transition-colors duration-200 group cursor-pointer"
                    onClick={() => setSelectedInquiry(inquiry)}
                  >
                    {/* Date & Time */}
                    <td className="py-4 px-6 font-semibold text-xs text-gray-500 whitespace-nowrap">
                      {inquiry.dateString}
                    </td>

                    {/* Name */}
                    <td className="py-4 px-6">
                      <div className="font-bold text-sugarcane-charcoal text-sm group-hover:text-sugarcane-green transition-colors flex items-center gap-2">
                        {inquiry.fullName}
                      </div>
                    </td>

                    {/* Contact Details */}
                    <td className="py-4 px-6 text-xs font-semibold text-gray-500 space-y-1">
                      <div className="flex items-center gap-1.5 hover:text-sugarcane-charcoal transition-colors" onClick={(e) => e.stopPropagation()}>
                        <Mail size={12} className="text-gray-400" />
                        <a href={`mailto:${inquiry.email}`}>{inquiry.email}</a>
                      </div>
                      <div className="flex items-center gap-1.5 hover:text-sugarcane-charcoal transition-colors" onClick={(e) => e.stopPropagation()}>
                        <Phone size={12} className="text-gray-400" />
                        <a href={`tel:${inquiry.phone}`}>{inquiry.phone}</a>
                      </div>
                    </td>

                    {/* Dynamic Columns per Tab */}
                    {inquiryTypeTab === 'product' ? (
                      <>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-extrabold bg-emerald-50 text-emerald-800 border border-emerald-100">
                            {inquiry.productName || 'Product Inquiry'}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <span className="text-xs font-bold text-sugarcane-charcoal bg-gray-100 px-2.5 py-1 rounded-md">
                            {inquiry.quantityRequired || 'N/A'}
                          </span>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-sugarcane-charcoal border border-gray-200/50">
                            {inquiry.subject || 'General Inquiry'}
                          </span>
                        </td>
                        <td className="py-4 px-6 max-w-xs">
                          <p className="text-xs text-gray-500 font-semibold line-clamp-1 italic">
                            "{inquiry.message}"
                          </p>
                        </td>
                      </>
                    )}

                    {/* Status Badge */}
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-xl text-[10px] font-bold uppercase tracking-wider border ${
                        inquiry.status === 'viewed' 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200/50' 
                          : 'bg-amber-50 text-amber-700 border-amber-200/50'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-2 ${inquiry.status === 'viewed' ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`}></span>
                        {inquiry.status}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="py-4 px-6" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => toggleStatus(inquiry.id, inquiry.status)}
                          className={`p-2 rounded-lg border transition-all duration-300 flex items-center justify-center cursor-pointer ${
                            inquiry.status === 'viewed'
                              ? 'bg-amber-50 border-amber-200/50 text-amber-600 hover:bg-amber-100 hover:scale-105 shadow-sm'
                              : 'bg-emerald-50 border-emerald-200/50 text-sugarcane-green hover:bg-emerald-100 hover:scale-105 shadow-sm'
                          }`}
                          title={inquiry.status === 'viewed' ? 'Mark as Pending' : 'Mark as Viewed'}
                        >
                          {inquiry.status === 'viewed' ? <Clock size={16} /> : <CheckCircle size={16} />}
                        </button>

                        <button
                          onClick={() => setSelectedInquiry(inquiry)}
                          className="p-2 bg-blue-50 border border-blue-200/50 text-blue-600 hover:bg-blue-100 rounded-lg transition-all shadow-sm flex items-center justify-center cursor-pointer"
                          title="View Inquiry Details"
                        >
                          <Eye size={16} />
                        </button>

                        <button
                          onClick={() => handleDelete(inquiry.id)}
                          className="p-2 bg-red-50 border border-red-200/50 text-red-500 hover:bg-red-100 rounded-lg transition-all shadow-sm flex items-center justify-center cursor-pointer"
                          title="Delete Submission"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inquiry Detail Premium Modal */}
      <AnimatePresence>
        {selectedInquiry && (
          <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-sugarcane-charcoal/80 backdrop-blur-md admin-modal-overlay">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 250 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl relative overflow-hidden border border-gray-100"
            >
              {/* Modal Banner */}
              <div className="bg-gradient-to-br from-sugarcane-charcoal to-gray-900 p-6 text-white relative">
                <div className="absolute top-0 left-0 w-full h-full bg-sugarcane-green/5 blur-xl"></div>
                <div className="relative z-10 flex justify-between items-start">
                  <div>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-[9px] font-bold uppercase tracking-wider text-sugarcane-green border border-white/10">
                      {selectedInquiry.productName ? 'Product Inquiry Detail' : 'Contact Us Submission Detail'}
                    </span>
                    <h3 className="text-xl font-bold mt-2 tracking-tight">
                      {selectedInquiry.fullName}
                    </h3>
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1.5 font-semibold">
                      <Calendar size={12} className="text-gray-500" />
                      Received on {selectedInquiry.dateString}
                    </p>
                  </div>
                  <button 
                    onClick={() => setSelectedInquiry(null)}
                    className="p-2 bg-white/5 hover:bg-white/10 rounded-xl text-white transition-colors border border-white/10 cursor-pointer"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
                {/* Status indicator row */}
                <div className="bg-gray-50/50 p-4 rounded-xl flex items-center justify-between border border-gray-200">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xs font-semibold tracking-wider text-gray-500">Current Status:</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-xl text-[10px] font-bold uppercase tracking-wider border ${
                      selectedInquiry.status === 'viewed' 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200/50' 
                        : 'bg-amber-50 text-amber-700 border-amber-200/50'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-2 ${selectedInquiry.status === 'viewed' ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`}></span>
                      {selectedInquiry.status}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleStatus(selectedInquiry.id, selectedInquiry.status)}
                      className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-lg border transition-all duration-300 flex items-center gap-1.5 cursor-pointer ${
                        selectedInquiry.status === 'viewed'
                          ? 'bg-amber-50 border-amber-200/50 text-amber-600 hover:bg-amber-100'
                          : 'bg-emerald-50 border-emerald-200/50 text-sugarcane-green hover:bg-emerald-100'
                      }`}
                    >
                      {selectedInquiry.status === 'viewed' ? <Clock size={12} /> : <CheckCircle size={12} />}
                      Mark as {selectedInquiry.status === 'viewed' ? 'Pending' : 'Viewed'}
                    </button>
                  </div>
                </div>

                {/* Product specific metadata if available */}
                {selectedInquiry.productName && (
                  <div className="bg-emerald-50/60 border border-emerald-200/60 p-4 rounded-xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-emerald-800 flex items-center gap-1.5">
                        <Box size={14} /> Requested Product:
                      </span>
                      <span className="text-sm font-extrabold text-emerald-900">
                        {selectedInquiry.productName}
                      </span>
                    </div>
                    {selectedInquiry.quantityRequired && (
                      <div className="flex items-center justify-between border-t border-emerald-200/40 pt-2">
                        <span className="text-xs font-bold text-emerald-700">Quantity Required:</span>
                        <span className="text-xs font-extrabold bg-white px-3 py-1 rounded-md text-emerald-900 shadow-xs">
                          {selectedInquiry.quantityRequired}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Contact Card */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Email & Phone */}
                  <div className="space-y-4">
                    <h4 className="text-xs font-semibold tracking-wider text-gray-500">Sender Contacts</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 bg-gray-50/50 border border-gray-200 p-3 rounded-xl">
                        <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shadow-inner">
                          <Mail size={16} />
                        </div>
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">Email Address</p>
                          <a href={`mailto:${selectedInquiry.email}`} className="text-xs font-bold text-sugarcane-charcoal hover:underline">
                            {selectedInquiry.email}
                          </a>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 bg-gray-50/50 border border-gray-200 p-3 rounded-xl">
                        <div className="w-10 h-10 bg-green-50 text-green-600 rounded-xl flex items-center justify-center shadow-inner">
                          <Phone size={16} />
                        </div>
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">Contact Number</p>
                          <a href={`tel:${selectedInquiry.phone}`} className="text-xs font-bold text-sugarcane-charcoal hover:underline">
                            {selectedInquiry.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subject Tag */}
                  <div className="space-y-4">
                    <h4 className="text-xs font-semibold tracking-wider text-gray-500">Submission Meta</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 bg-gray-50/50 border border-gray-200 p-3 rounded-xl">
                        <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center shadow-inner">
                          <Tag size={16} />
                        </div>
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">Subject / Category</p>
                          <p className="text-xs font-extrabold text-sugarcane-charcoal">{selectedInquiry.subject || 'Product Inquiry'}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 bg-gray-50/50 border border-gray-200 p-3 rounded-xl">
                        <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center shadow-inner">
                          <User size={16} />
                        </div>
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">Database Identifier</p>
                          <p className="text-[10px] font-bold text-gray-500 font-mono truncate max-w-[180px]">{selectedInquiry.id}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inquiry Message Text */}
                {selectedInquiry.message && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold tracking-wider text-gray-500">Message Content</h4>
                    <div className="bg-gray-50/50 border border-gray-200 p-4 rounded-xl">
                      <p className="text-xs text-sugarcane-charcoal font-semibold leading-relaxed whitespace-pre-line italic">
                        "{selectedInquiry.message}"
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Action Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-3 justify-end items-center">
                <button
                  onClick={() => handleDelete(selectedInquiry.id)}
                  className="w-full sm:w-auto px-6 py-3.5 bg-red-50 border border-red-200 text-red-600 hover:bg-red-500 hover:text-white rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Trash2 size={14} />
                  Delete Inquiry
                </button>
                <button
                  onClick={() => setSelectedInquiry(null)}
                  className="w-full sm:w-auto px-6 py-3.5 bg-sugarcane-charcoal text-white hover:bg-sugarcane-green rounded-xl font-semibold text-sm transition-all flex items-center justify-center cursor-pointer"
                >
                  Close details
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default AdminInquiries;
